import { Modal } from '../../../components/modal/modal';
import { useEffect, useState } from 'react';
import { Button } from '../../../components/form/button';
import {
  Order,
  OrderSource,
  OrderStatus,
  OrderStatusTransitionPaymentRequirement,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { Spinner } from '../../../components/spinner/spinner';
import { toast } from '../../../util/toasts';
import { PaymentMethodRequestV2 } from '../../../clients/quipli-service/payment/payment-v2';
import { Toggle } from '../../../components/form/toggle';
import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import { Invoice } from '../../../clients/quipli-service/invoice/invoice';
import { Payment, PaymentMethodType, StripeCard } from '../../../clients/quipli-service/payment/payment';
import { keyBy, round } from 'lodash';
import './status-transitions.scss';
import { ValidationError } from '../../../components/validation-error/validation-error';
import classNames from 'classnames';
import { displayMoneyFormat } from '../../../util/util';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { orderToPaymentMethodV2, StatusTransitionPayment } from './status-transition-payment';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';

export const ApproveOrder = ({
  orderId,
  onCancel,
  onComplete,
  card,
}: {
  orderId: number;
  onCancel: () => void;
  onComplete: () => void;
  card?: StripeCard;
}) => {
  const { approveOrder, canApproveOrder, getStatusTransitionPaymentRequirement, getOrderById } = useOrderApiV1();
  const { createPayment } = usePaymentApiV2();
  const { checkUserDriverLicenseUniqueness, getMultipleConfigurations } = useAPI();
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>();
  const [invoice, setInvoice] = useState<Invoice | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2 | undefined>();
  const [approveWithPayment, setApproveWithPayment] = useState<boolean>(true);
  const [amount, setAmount] = useState<string>();
  const [statusTransitionPaymentRequirement, setStatusTransitionPaymentRequirement] =
    useState<Partial<OrderStatusTransitionPaymentRequirement>>();
  const [canApprove, setCanApprove] = useState<{ isValid: boolean; errors: string[] }>();
  const [isLicenseUnique, setIsLicenseUnique] = useState<{ isValid: boolean; error: string }>();
  const hasSaleItems = order?.products.some((p) => p.type === OrderType.Sale) || false;
  const hasRentalItems = order?.products.some((p) => p.type === OrderType.Rental) || false;
  const [payments, setPayments] = useState<Payment[]>();
  const [facilitate, setFacilitate] = useState<boolean>(false);
  const [configs, setConfigs] = useState<{ [key: string]: Configuration }>();
  const requiresFullPayment =
    hasSaleItems &&
    !hasRentalItems &&
    !order?.isCycleBilled &&
    order?.paymentMethod.type !== PaymentMethodType.OnAccount;

  useEffect(() => {
    getOrderById(orderId)
      .then((_order) => {
        setOrder(_order);

        if (_order.source === OrderSource.Online) {
          checkUserDriverLicenseUniqueness(_order.user.id).then(setIsLicenseUnique);
        }
      })
      .catch((e) => toast(e.message, 'error'));
    canApproveOrder(orderId)
      .then(setCanApprove)
      .catch((e) => toast(e.message, 'error'));
    getStatusTransitionPaymentRequirement(OrderStatus.Approved).then((tpr) =>
      setStatusTransitionPaymentRequirement(tpr || {}),
    );
    getMultipleConfigurations([
      'CONF_UNIT_TRACKING_VIA',
      'CONF_SEIS_INTEGRATION',
      'CONF_CREATE_SEIS_CERTS_ON_RENT',
    ]).then((_configs) => setConfigs(keyBy(_configs, 'variable')));
  }, []);

  useEffect(() => {
    if (invoice) {
      if (invoice.balance <= 0) {
        setApproveWithPayment(false);
      }
    }
  }, [invoice]);

  useEffect(() => {
    if (order) {
      if (order.isCycleBilled || order.paymentMethod.type === PaymentMethodType.OnAccount) {
        setApproveWithPayment(false);
      }
      setPaymentMethod(orderToPaymentMethodV2(order));
      setInvoice(order.invoices.find((inv) => inv));
    }
  }, [order]);

  async function postPayment(payments?: Payment[]) {
    setFacilitate(false);
    if (payments) {
      if (payments[0].gatewayStatus === 'requires_action') {
        toast('Payment requires authentication', 'error');
      } else if (payments[0]?.gatewayStatus == 'processing') {
        toast('Bank payment is processing. Once approved you will be able to approve the order');
      } else if (payments[0]?.status === 'failed') {
        toast('Payment failed', 'error');
      }
    }
    try {
      await approveOrder(orderId);
      onComplete();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setIsApproving(false);
    }
  }

  async function doApprove() {
    try {
      setIsApproving(true);
      if (approveWithPayment && invoice && paymentMethod && amount && parseFloat(amount) > 0) {
        const payment = await createPayment({
          invoiceIds: [invoice.id],
          paymentMethod,
          amount: parseFloat(amount),
          userId: invoice.customer.id,
        });

        if (!payment) {
          toast('Payment failed', 'error');
          return;
        }

        if (paymentMethod.type === PaymentMethodType.Stripe && paymentMethod.terminalId) {
          setFacilitate(true);
          setPayments([payment]);
          return;
        } else {
          if (payment.gatewayStatus === 'requires_action') {
            toast('Payment requires authentication', 'error');
          } else if (payment.gatewayStatus == 'processing') {
            toast('Bank payment is processing. Once approved you will be able to approve the order');
            return;
          } else if (payment.status === 'failed') {
            toast('Payment failed', 'error');
            return;
          }
        }
      }
      await approveOrder(orderId);
      onComplete();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setIsApproving(false);
    }
  }

  const numericAmount = amount !== undefined && parseFloat(amount);
  const requiresAdditionalPayment =
    order &&
    order.paymentMethod.type !== PaymentMethodType.OnAccount &&
    !order.isCycleBilled &&
    invoice &&
    statusTransitionPaymentRequirement?.requiredPaymentPercentage !== undefined &&
    statusTransitionPaymentRequirement.requiredPaymentPercentage >
      round(
        (100 * (invoice.total - invoice.balance + (approveWithPayment ? numericAmount || 0 : 0))) / invoice.total,
        2,
      );
  const paymentGreaterThanBalance = !!numericAmount && invoice && numericAmount > invoice.balance;
  const isValid = !(requiresAdditionalPayment || paymentGreaterThanBalance || (canApprove && !canApprove.isValid));
  const hasPaymentsPendingAuthentication = invoice?.payments.some((p) => p.gatewayStatus === 'requires_action');
  const displayWarnings =
    !isValid ||
    (requiresFullPayment && hasSaleItems && !hasRentalItems) ||
    hasPaymentsPendingAuthentication ||
    isLicenseUnique?.isValid === false;

  return (
    <Modal
      isOpen={true}
      title="Approve Order"
      actions={
        !order || isApproving ? null : (
          <>
            <Button className="button button-secondary" onClick={() => onCancel()}>
              Cancel
            </Button>
            <Button
              dataCy="approve-order-modal"
              className="button button-yellow"
              disabled={!isValid}
              onClick={() => doApprove()}
            >
              {`Approve${isValid && isLicenseUnique?.isValid === false ? ' Anyway' : ''}`}
            </Button>
          </>
        )
      }
      close={() => onCancel()}
    >
      {!order ||
      !configs ||
      (isApproving && !(paymentMethod?.type == PaymentMethodType.Stripe && paymentMethod.terminalId)) ? (
        <Spinner />
      ) : (
        <div className="flex flex-column w-full">
          {/* SEIS integration unit validation/warnings */}
          {configs['CONF_SEIS_INTEGRATION']?.value === '1' && configs['CONF_CREATE_SEIS_CERTS_ON_RENT']?.value === '0' && (
            <>
              {(!order.user.phone || !order.user.email || !order.billingAddress) && (
                <ValidationError>
                  <ul>
                    <li>
                      Customer must have a phone number, email address, and billing address to create the rental damage
                      waiver insurance.
                    </li>
                  </ul>
                </ValidationError>
              )}
              {order.products.map((op) => {
                if (!op.product.seisRdw || op.type === OrderType.Sale) {
                  return <></>;
                }
                return (
                  <>
                    {op.assignedUnits.map((au) => {
                      {
                        (!au.serialNumber && !au.unitNumber) ||
                          !au.classification ||
                          (!order.isCycleBilled && !op.rentalEndDate && (
                            <ValidationError className="warning">
                              <ul>
                                <li>
                                  {op.product.name}{' '}
                                  {au[configs['CONF_UNIT_TRACKING_VIA']?.value as 'unitNumber' | 'serialNumber']} is
                                  missing required information for SEIS (serial or unit number, year, make, model, and
                                  rental end date).
                                </li>
                              </ul>
                            </ValidationError>
                          ));
                      }
                    })}
                  </>
                );
              })}
            </>
          )}
          {invoice && invoice.balance > 0 ? (
            <div>
              <div className="flex gap-2 text-[0.9rem]">
                <Toggle
                  onClick={() => {
                    setApproveWithPayment((prev) => !prev);
                  }}
                  checked={approveWithPayment}
                  disabled={requiresFullPayment}
                />
                Approve with Payment
              </div>
              {approveWithPayment && (
                <StatusTransitionPayment
                  order={order}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  total={invoice.total}
                  paymentTotal={invoice.total - invoice.balance}
                  amount={amount}
                  setAmount={setAmount}
                  defaultPercentage={
                    requiresFullPayment ? 100 : statusTransitionPaymentRequirement?.defaultPaymentPercentage || 0
                  }
                  card={card}
                  payments={payments}
                  setPayments={setPayments}
                  onComplete={(payments) => {
                    postPayment(payments);
                  }}
                  facilitate={facilitate}
                />
              )}
            </div>
          ) : (
            <div>Order has been fully paid</div>
          )}
          <ValidationError className={classNames({ hidden: !displayWarnings })} style={{ maxWidth: '342px' }}>
            <ul>
              {requiresFullPayment && hasSaleItems && !hasRentalItems && (
                <li>Orders containing only sale items must be fully paid on approval</li>
              )}
              {requiresAdditionalPayment && (
                <li>
                  At least {statusTransitionPaymentRequirement?.requiredPaymentPercentage}% of this order must be paid
                  to approve
                </li>
              )}
              {paymentGreaterThanBalance && (
                <li>Cannot collect more than the order balance of {displayMoneyFormat(invoice.balance, 'usd')}</li>
              )}
              {canApprove?.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {invoice?.payments.some((p) => p.gatewayStatus === 'requires_action') && (
                <li>Existing payments require authentication</li>
              )}
              {invoice?.payments.some((p) => p.gatewayStatus === 'processing') && (
                <li>Existing payments are pending</li>
              )}
              {isLicenseUnique?.isValid === false && <li>{isLicenseUnique.error}</li>}
            </ul>
          </ValidationError>
        </div>
      )}
    </Modal>
  );
};
